import { ExternalLink } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Trans } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "~/components/ui/button";
import {
	FormCard,
	FormCardBody,
	FormCardFooter,
	FormCardHeader,
	FormCardHeaderSubtitle,
	FormCardHeaderTitle,
} from "~/components/ui/form-card";
import { Label } from "~/components/ui/label";
import { useCopy } from "~/hooks/use-copy";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute("/_app/podcasts/$channelUid/settings/share")({
	component: Page,
});

function Page() {
	const { channelUid } = Route.useParams();
	const trpc = useTRPC();
	const { handleCopy } = useCopy();

	const { data: channel } = useSuspenseQuery(trpc.channel.find.queryOptions({ channelUid }));

	const embedCode = `<iframe
		title="Podcast player for channel ${channelUid}"
		style="width: 100%; height: 80px;"
		src="${import.meta.env.VITE_EMBED_APP_URL}/${channelUid}"
></iframe>`;

	return (
		<div className="grid w-full gap-6 px-4">
			<FormCard>
				<FormCardHeader>
					<div>
						<FormCardHeaderTitle>
							<Trans>Share this show</Trans>
						</FormCardHeaderTitle>
						<FormCardHeaderSubtitle>
							<Trans>
								Get the URL or the embed code for your podcast to share it with your audience or
								embed it on your website.
							</Trans>
						</FormCardHeaderSubtitle>
					</div>
				</FormCardHeader>
				<FormCardBody>
					<section className="space-y-6">
						<div className="grid gap-2">
							<div className="flex gap-3">
								<div className="flex grow flex-col gap-2">
									<div className="flex-1">
										<div className="flex items-center justify-between">
											<Label>
												<Trans>Podcast Page</Trans>
											</Label>
										</div>
										<div className="text-muted-foreground mt-1 text-sm">
											<Trans>
												Link to the public page of your podcast, where listeners can find more
												information about your show and listen to episodes.
											</Trans>
										</div>
									</div>
								</div>
								<Button variant="outline" asChild>
									<a
										href={`${import.meta.env.VITE_SHOWS_APP_URL}/${channel.urlTitle}`}
										rel="noopener"
										target="_blank"
									>
										<Trans>View Podcast Page</Trans>
										<HugeiconsIcon icon={ExternalLink} />
									</a>
								</Button>
							</div>
						</div>
					</section>
				</FormCardBody>
			</FormCard>
			<FormCard>
				<FormCardHeader>
					<div>
						<FormCardHeaderTitle>
							<Trans>Embed this show</Trans>
						</FormCardHeaderTitle>
						<FormCardHeaderSubtitle>
							<Trans>
								Embed the podcast player on your own website or blog to allow visitors to listen to
								your episodes without leaving your site.
							</Trans>
						</FormCardHeaderSubtitle>
					</div>
					<div className="ml-auto">
						<Button variant="outline" onClick={() => handleCopy(embedCode)}>
							<Trans>Copy Code</Trans>
						</Button>
					</div>
				</FormCardHeader>
				<FormCardBody>
					<pre className="max-w-full whitespace-pre-wrap">
						<code>{embedCode}</code>
					</pre>
					<p>
						Copy the code above and paste it into your website's HTML to embed the podcast player.
					</p>
					<p>
						The iframe is responsive and will adjust its size based on the container it is placed
						in. The recommended minimum height is 80px.
					</p>
				</FormCardBody>
				<FormCardBody
					className="mt-1"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: This is a trusted embed code generated by our own system, and we need to render it as HTML to display the podcast player.
					dangerouslySetInnerHTML={{ __html: embedCode }}
				></FormCardBody>
				<FormCardFooter>
					<p>
						<Trans>
							Unless specified otherwise, the iframe will load the latest episode available. You can
							get the code for an episode-specific embed in the episode details page.
						</Trans>
					</p>
				</FormCardFooter>
			</FormCard>
		</div>
	);
}
